import { MAX_PREVIEW_SIDE } from "components/SmartSurveyor/ReviewPage/FloorPlanPanelPdf/constants";
import { computeNotePageScale } from "../computeNotePageScale";
import { buildMoncoBrandBlock } from "../drawMoncoBrandLink";
import { computeNoteMetrics, layoutNotePages } from "../layoutNotePages";
import { rasterizeSvgOntoCanvas } from "../rasterizeSvgOntoCanvas";
import { renderNotePageSvgString } from "../renderNotePageSvgString";
import {
  NOTE_PAGE_BACKGROUND,
  NOTE_PAGE_JPEG_QUALITY,
  NOTE_PAGE_MARGIN,
} from "./constants";
import type { AppendNotePages } from "./types";

export const appendNotePages: AppendNotePages = async ({
  doc,
  noteAppendix,
  entries,
  crops,
  pdfPageWidth,
  pdfPageHeight,
}) => {
  if (entries.length === 0) return;

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Failed to get 2D context for note pages canvas");
  }
  const renderScale = Math.min(
    MAX_PREVIEW_SIDE / pdfPageWidth,
    MAX_PREVIEW_SIDE / pdfPageHeight,
  );
  canvas.width = Math.round(pdfPageWidth * renderScale);
  canvas.height = Math.round(pdfPageHeight * renderScale);
  context.imageSmoothingQuality = "high";

  try {
    const scale = computeNotePageScale({ pdfPageWidth, pdfPageHeight });
    const brandBlock = await buildMoncoBrandBlock({
      doc,
      pdfPageWidth,
      pdfPageHeight,
    });
    const metrics = computeNoteMetrics({
      scale,
      pdfPageWidth,
      pdfPageHeight,
      titleTop: brandBlock.bottom + NOTE_PAGE_MARGIN * scale,
    });
    const pages = layoutNotePages({
      entries,
      regions: crops.regions,
      metrics,
      context,
    });

    const orientation =
      pdfPageWidth >= pdfPageHeight ? "landscape" : "portrait";
    for (const [pageIndex, page] of pages.entries()) {
      context.fillStyle = NOTE_PAGE_BACKGROUND;
      context.fillRect(0, 0, canvas.width, canvas.height);

      // Crops go straight onto the canvas; the SVG above them carries only vectors and text.
      for (const segment of page.segments) {
        if (segment.kind !== "head") continue;
        const cell = crops.cells[segment.entryIndex];
        context.drawImage(
          crops.atlas,
          cell.x,
          cell.y,
          cell.side,
          cell.side,
          segment.crop.x * renderScale,
          segment.crop.y * renderScale,
          segment.crop.side * renderScale,
          segment.crop.side * renderScale,
        );
      }

      const svgString = renderNotePageSvgString({
        pdfPageWidth,
        pdfPageHeight,
        pixelWidth: canvas.width,
        pixelHeight: canvas.height,
        metrics,
        title: noteAppendix.title,
        pageLabel: `${pageIndex + 1} / ${pages.length}`,
        continuedLabel: noteAppendix.continuedLabel,
        entries,
        page,
      });
      await rasterizeSvgOntoCanvas({
        svgString,
        context,
        canvasWidth: canvas.width,
        canvasHeight: canvas.height,
      });

      doc.addPage([pdfPageWidth, pdfPageHeight], orientation);
      // TODO: toBlob instead of toDataURL, as on the plan page, so the spinner keeps running between pages.
      doc.addImage(
        canvas.toDataURL("image/jpeg", NOTE_PAGE_JPEG_QUALITY),
        "JPEG",
        0,
        0,
        pdfPageWidth,
        pdfPageHeight,
      );
      brandBlock.draw();
    }
  } finally {
    // Shrinking the canvases releases their pixel buffers before the PDF is serialized.
    canvas.width = 1;
    canvas.height = 1;
    crops.atlas.width = 1;
    crops.atlas.height = 1;
  }
};
